import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    applyDashboardFilters, DASHBOARD_STORAGE_KEY, DEFAULT_DASHBOARD_FILTERS,
    parseStoredDashboardFilters, resolveDashboardFilters,
} from "./dashboardFilters";
import type { DashboardFilters } from "./dashboardFilters";

export function useDashboardFilters(enabled: boolean) {
    const [params, setParams] = useSearchParams();
    const paramsString = params.toString();
    const [initial] = useState(() => {
        try {
            return { filters: resolveDashboardFilters(params, parseStoredDashboardFilters(localStorage.getItem(DASHBOARD_STORAGE_KEY))), error: null };
        } catch (error: unknown) {
            return {
                filters: resolveDashboardFilters(params),
                error: `Unable to restore dashboard filters: ${error instanceof Error ? error.message : "Storage unavailable"}`,
            };
        }
    });
    const [filters, setFilters] = useState(initial.filters);
    const [error, setError] = useState<string | null>(initial.error);
    const latestParams = useRef(params);
    latestParams.current = params;
    const latestFilters = useRef(filters);
    latestFilters.current = filters;
    const lastSearch = useRef(paramsString);
    const observedSearch = useRef(paramsString);
    const initialized = useRef(false);
    const searchDirty = useRef(false);

    const persist = useCallback((next: DashboardFilters) => {
        try {
            localStorage.setItem(DASHBOARD_STORAGE_KEY, JSON.stringify({ q: next.q.trim(), cat: next.cat }));
        } catch (error: unknown) {
            setError(`Unable to save dashboard filters: ${error instanceof Error ? error.message : "Storage unavailable"}`);
        }
    }, []);
    const commit = useCallback((next: DashboardFilters) => {
        const nextParams = applyDashboardFilters(latestParams.current, next);
        lastSearch.current = nextParams.toString();
        if (nextParams.toString() !== latestParams.current.toString()) {
            latestParams.current = nextParams;
            setParams(nextParams, { replace: true });
        }
        persist(next);
    }, [persist, setParams]);

    useEffect(() => {
        if (!enabled) return;
        if (!initialized.current) {
            initialized.current = true;
            const next = paramsString === lastSearch.current ? latestFilters.current : resolveDashboardFilters(params);
            latestFilters.current = next;
            setFilters(next);
            commit(next);
        } else if (paramsString !== observedSearch.current) {
            observedSearch.current = paramsString;
            if (paramsString === lastSearch.current) return;
            // Browser navigation is authoritative and cancels any pending search draft.
            lastSearch.current = paramsString;
            searchDirty.current = false;
            const next = resolveDashboardFilters(params, DEFAULT_DASHBOARD_FILTERS);
            latestFilters.current = next;
            setFilters(next);
            persist(next);
        }
    }, [enabled, paramsString, params, commit, persist]);

    useEffect(() => {
        if (!enabled || !searchDirty.current) return;
        const timeout = setTimeout(() => {
            if (!searchDirty.current) return;
            searchDirty.current = false;
            commit(latestFilters.current);
        }, 300);
        return () => clearTimeout(timeout);
    }, [enabled, filters.q, paramsString, commit]);

    const setSearchQuery = (q: string) => {
        searchDirty.current = true;
        const next = { ...latestFilters.current, q };
        latestFilters.current = next;
        setFilters(next);
    };
    const setCategory = (cat: string) => {
        const next = { ...latestFilters.current, cat };
        latestFilters.current = next;
        setFilters(next);
        // Include the current draft so a category change cannot overwrite it.
        searchDirty.current = false;
        commit(next);
    };
    return { searchQuery: filters.q, category: filters.cat, setSearchQuery, setCategory, error };
}
